import { useEffect, useRef } from 'react';

/**
 * useTabNotification
 *
 * Quando o usuário está fora da aba e chega nova mensagem:
 *  - Título da aba pisca com nome do contato
 *  - Favicon ganha badge vermelho
 *  - Notificação nativa do OS (se permissão concedida)
 *
 * Tudo para automaticamente quando o usuário volta para a aba.
 */
export function useTabNotification(newMessage) {
  const blinkRef        = useRef(null);
  const originalTitle   = useRef(document.title);
  const originalFavicon = useRef(null);

  // Captura favicon original + pede permissão de notificação eagerly
  useEffect(() => {
    const link = document.querySelector("link[rel~='icon']");
    originalFavicon.current = link?.href || '/favicon.ico';

    // Pede permissão na montagem — não na hora da mensagem — para não perder o timing
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Para o blink quando o usuário volta para a aba
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) stopAll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dispara quando chega nova mensagem
  useEffect(() => {
    if (!newMessage) return;
    if (document.hidden) {
      startBlink(newMessage.contact);
      tryNativeNotification(newMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newMessage]);

  // ── funções internas ────────────────────────────────────────────────────────

  function startBlink(contact) {
    if (blinkRef.current) return; // já piscando
    originalTitle.current = document.title;
    let toggle = true;
    blinkRef.current = setInterval(() => {
      document.title = toggle
        ? `🔔 ${contact} — nova mensagem`
        : originalTitle.current;
      toggle = !toggle;
    }, 1000);
    setFaviconBadge(true);
  }

  function stopAll() {
    if (blinkRef.current) {
      clearInterval(blinkRef.current);
      blinkRef.current = null;
    }
    document.title = originalTitle.current;
    setFaviconBadge(false);
  }

  function setFaviconBadge(active) {
    const canvas  = document.createElement('canvas');
    canvas.width  = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.drawImage(img, 0, 0, 32, 32);
      if (active) {
        // Badge vermelho no canto superior direito
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.arc(26, 6, 7, 0, 2 * Math.PI);
        ctx.fill();
        // Borda branca
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = canvas.toDataURL('image/png');
    };
    img.onerror = () => {
      // favicon não carregou — não trava a feature
    };
    img.src = originalFavicon.current;
  }

  function tryNativeNotification(message) {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    try {
      const n = new Notification(`💬 ${message.contact}`, {
        body:     message.preview || 'Nova mensagem',
        icon:     '/favicon.ico',
        tag:      `chatwoot-${message.conversationId}`,
        renotify: true, // re-exibe mesmo se tag já existia
      });
      // Clique na notificação traz o tab ao foco
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch (_) {}
  }
}
